import React, { useCallback, useEffect } from 'react';
import {
  isImageFile,
  isVideoFile,
  isAudioFile,
  isPdfFile,
  isTextFile,
  getFileIcon,
  getFileUrl,
} from '../utils/fileUtils';

interface FileViewerProps {
  /** Список имён файлов (без папки) */
  files: string[];
  /** Текущий индекс */
  currentIndex: number;
  /** Папка на сервере, напр. 'notifications' или 'tasks' */
  folder: string;
  /** Закрыть просмотрщик */
  onClose: () => void;
  /** Перейти к следующему */
  onNext: () => void;
  /** Перейти к предыдущему */
  onPrev: () => void;
  /** Скачать текущий файл */
  onDownload?: (filename: string) => void;
}

const FileViewer: React.FC<FileViewerProps> = ({
  files,
  currentIndex,
  folder,
  onClose,
  onNext,
  onPrev,
  onDownload,
}) => {
  const filename = files[currentIndex] || '';
  const fileUrl = getFileUrl(folder, filename);
  const total = files.length;

  // Keyboard navigation
  const handleKey = useCallback((e: KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowLeft':  e.preventDefault(); onPrev(); break;
      case 'ArrowRight': e.preventDefault(); onNext(); break;
      case 'Escape':     e.preventDefault(); onClose(); break;
    }
  }, [onPrev, onNext, onClose]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [handleKey]);

  const renderContent = () => {
    if (isImageFile(filename)) {
      return (
        <img
          src={fileUrl}
          alt="Preview"
          className="file-viewer__image"
          draggable={false}
        />
      );
    }
    if (isVideoFile(filename)) {
      return (
        <video controls className="file-viewer__video">
          <source src={fileUrl} />
          Ваш браузер не поддерживает видео.
        </video>
      );
    }
    if (isAudioFile(filename)) {
      return (
        <div className="file-viewer__audio-wrap">
          <div className="file-viewer__big-icon">🎵</div>
          <audio controls className="file-viewer__audio">
            <source src={fileUrl} />
            Ваш браузер не поддерживает аудио.
          </audio>
          <p className="file-viewer__filename">{filename}</p>
        </div>
      );
    }
    if (isPdfFile(filename) || isTextFile(filename)) {
      return (
        <iframe
          src={fileUrl}
          title="File Preview"
          className="file-viewer__iframe"
        />
      );
    }
    // Unsupported — offer download
    return (
      <div className="file-viewer__unsupported">
        <div className="file-viewer__big-icon">{getFileIcon(filename)}</div>
        <h3 className="file-viewer__unsupported-name">{filename}</h3>
        <p className="file-viewer__unsupported-hint">
          Этот тип файла нельзя просмотреть в браузере
        </p>
        {onDownload && (
          <button
            className="btn btn-primary"
            onClick={() => onDownload(filename)}
          >
            Скачать файл
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="file-viewer-overlay" onClick={onClose}>
      {/* Content area */}
      <div
        className="file-viewer-content"
        onClick={(e) => e.stopPropagation()}
      >
        {renderContent()}

        {/* Close button */}
        <button
          className="file-viewer__close"
          onClick={onClose}
          aria-label="Закрыть"
        >
          ×
        </button>
      </div>

      {/* Navigation */}
      {total > 1 && (
        <>
          <button
            className="file-viewer__nav file-viewer__nav--prev"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            disabled={currentIndex === 0}
            title="Предыдущий (←)"
          >
            ‹
          </button>
          <button
            className="file-viewer__nav file-viewer__nav--next"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            disabled={currentIndex === total - 1}
            title="Следующий (→)"
          >
            ›
          </button>
        </>
      )}

      {/* Counter */}
      <div
        className="file-viewer__counter"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="file-viewer__counter-pos">
          {total > 1 ? `${currentIndex + 1} из ${total}` : 'Единственный файл'}
        </span>
        <span className="file-viewer__counter-name">{filename}</span>
      </div>
    </div>
  );
};

export default FileViewer;
